import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import { LoadingButton } from "@mui/lab";
import { Box, List, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import * as React from "react";

import { useAnalytics } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { RobotoLogo, RobotoTypography } from "@/shared/components";
import { Experience } from "@/shared/domain/experiences";
import { ingestionRulesQueryKeys } from "@/shared/domain/ingestionRules/hooks";
import { LoggerService } from "@/shared/services/LoggerService";

import { AuthCard } from "./AuthCard";
import { DefaultRuleChoices } from "./DefaultRuleChoices";
import { ExperienceToggle } from "./ExperienceToggle";
import { LegalFooter } from "./LegalFooter";
import { usePickedDefaultRules } from "./usePickedDefaultRules";

const buttonWidth = 215;

interface ConfigureExperiencesProps {
  isVisible: boolean;
  continueClicked: () => void;
  orgId?: string;
}

export const ConfigureExperiences: React.FC<ConfigureExperiencesProps> = ({
  isVisible,
  continueClicked,
  orgId,
}) => {
  if (!isVisible || !orgId) {
    return null;
  }
  return <GettingStarted orgId={orgId} continueClicked={continueClicked} />;
};

function GettingStarted({
  orgId,
  continueClicked,
}: {
  orgId: string;
  continueClicked: () => void;
}) {
  const theme = useTheme();
  const { experienceService } = useDomainServices();
  const queryClient = useQueryClient();

  const [dataset, setDataset] = useState(true);
  const ruleChoices = usePickedDefaultRules(orgId);

  const [loading, setLoading] = useState<boolean>(false);
  const [buttonText, setButtonText] = useState<string>("Finish");

  const { trackEvent } = useAnalytics();

  return (
    <>
      <RobotoLogo sx={{ maxWidth: "250px" }} />

      <AuthCard>
        <Box sx={{ textAlign: "center", mb: theme.spacing(3) }}>
          <SettingsSuggestIcon
            color="primary"
            sx={{
              fontSize: "5rem",
              display: {
                xs: "none",
                sm: "inline",
              },
            }}
          />

          <RobotoTypography
            variant="h5"
            textAlign={"center"}
            sx={{
              marginBottom: theme.spacing(2),
            }}
          >
            Getting Started
          </RobotoTypography>
          <RobotoTypography variant="body2" textAlign={"center"}>
            Configure default options in your account to streamline your
            experience. You can enable these later as well.
          </RobotoTypography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: theme.spacing(1),
            gap: theme.spacing(2),
            width: "100%",
          }}
        >
          <List>
            <ExperienceToggle
              displayText="Create initial dataset"
              description="A dataset with example log data will be created in your account"
              enabled={dataset}
              onChange={() => setDataset(!dataset)}
            />
          </List>
        </Box>
        <DefaultRuleChoices
          rules={ruleChoices.rules}
          isLoading={ruleChoices.isLoading}
          picked={ruleChoices.picked}
          onPickedChange={ruleChoices.setPicked}
        />
        <Box
          sx={{
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            mt: theme.spacing(2),
            width: { xs: "100%", sm: buttonWidth },
          }}
        >
          <LoadingButton
            data-cy={"finish-button"}
            disableElevation
            loading={loading}
            loadingPosition="end"
            onClick={() => {
              setLoading(true);
              setButtonText("Finalizing setup...");

              trackEvent("FinishAuthFlowClicked");
              const applyExperiencesRequest = {
                experiences: [
                  {
                    experience_id: Experience.GettingStarted,
                    config: {
                      dataset,
                      // Undefined until the defaults load, which leaves the
                      // choice to the server.
                      ingestion_rules: ruleChoices.picked,
                    },
                  },
                ],
              };
              experienceService
                .applyExperiences(orgId, applyExperiencesRequest)
                .then(() => {
                  // Stale, not refetched: the cached defaults still list the
                  // picked rules as not present until the worker creates them.
                  void queryClient.invalidateQueries({
                    queryKey: ingestionRulesQueryKeys.all(orgId),
                    refetchType: "none",
                  });
                  return new Promise<void>((resolve) => {
                    // Wait a few seconds to let the async experience operation kick off
                    setTimeout(() => {
                      setButtonText("All done!");
                      resolve();
                    }, 3000);
                  });
                })
                .catch((err) => {
                  setButtonText(
                    "Failed to apply the 'Getting Started' experience",
                  );
                  LoggerService.error("Failed to apply experiences", err);
                })
                .finally(() => {
                  setLoading(false);
                  continueClicked();
                });
            }}
            variant="contained"
            style={{
              textTransform: "none",
            }}
            sx={{
              marginBottom: theme.spacing(2),
              width: { xs: "100%", sm: buttonWidth },
            }}
          >
            <span>{buttonText}</span>
          </LoadingButton>
        </Box>
      </AuthCard>
      <LegalFooter />
    </>
  );
}
